import React, { useState } from "react";
import { Box, Button, Typography, CircularProgress, Container, Grid, Paper } from "@mui/material";
import { useNavigate } from "react-router-dom";
import Banner from "../../components/Banner";
import LegalLinks from "../../components/LegalLinks";
import { useMessage } from "../../components/MessageProvider";
import { setAnonymousSession } from "../../lib/anonymousSession";
import { useApi } from "../../hooks/useApi";
import { useAuth } from "../../contexts/AuthContext";

export default function Home() {
  const navigate = useNavigate();
  const { showError } = useMessage();
  const { createFreeAccount } = useApi();
  const { user, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showVideoFallback, setShowVideoFallback] = useState(false);

  const audiences = [
    {
      title: "Accountants",
      description: "Analyze journal entries before posting them into your ERP or general ledger system.",
    },
    {
      title: "Accounting Students",
      description: "Practice debits and credits in a visual T-account environment while learning accounting fundamentals.",
    },
    {
      title: "CPA Candidates",
      description: "Work through practice problems and complex transactions while studying for certification exams.",
    },
    {
      title: "Accounting Instructors",
      description: "Demonstrate journal entries and account balances clearly while teaching students.",
    },
    {
      title: "Finance & Accounting Teams",
      description: "Collaborate on unusual or complex transactions before entering them into the accounting system.",
    },
  ];

  const features = [
    {
      title: "Command Entry",
      description: 'Type commands like "Debit Cash 100" or "Credit Sales 100".',
    },
    {
      title: "Automatic T-Accounts",
      description: "Accounts update instantly as entries are added.",
    },
    {
      title: "Balance Validation",
      description: "Ensure debits and credits stay balanced by displaying entry IDs where debits ≠ credits.",
    },
    {
      title: "Export to file",
      description: "You can export your entries to Excel, PDF, or PNG to use as support.",
    },
    {
      title: "Built for Analysis",
      description: "Perfect for testing entries before posting to your ERP.",
    },
  ];

  const handleTryForFree = async () => {
    try {
      setLoading(true);

      const result = await createFreeAccount();
      if ('error' in result) {
        return;
      }

      const data = result.data;
      
      // Store session in localStorage
      setAnonymousSession(data.user_id, data.account_id);
      
      // Navigate to the first document
      navigate(`/sheet/${data.document_id}`);
    } catch (error) {
      showError(error.message || 'Error creating account');
    } finally {
      setLoading(false);
    }
  };

  const shouldShowTryForFree = !authLoading && !user;

  return (
    <Box minHeight="100vh" width="100%" bgcolor="#f4f6f7" color="#111827">
      <Banner />
      <Box pt="60px">
        <Box component="section" sx={{ py: { xs: 8, md: 12 }, color: "#111827" }}>
          <Container maxWidth="md" sx={{ textAlign: "center" }}>
            <Typography
              variant="h2"
              sx={{
                fontWeight: 800,
                letterSpacing: "-0.02em",
                mb: 2,
                fontSize: { xs: "2rem", md: "3.05rem" },
              }}
            >
              The Fastest Way to Think in T-Accounts.
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: "#5b6470",
                maxWidth: "920px",
                mx: "auto",
                mb: 5,
                fontSize: { xs: "0.95rem", md: "1.05rem" },
                fontWeight: 400,
              }}
            >
              Analyze journal entries using a prompt interface instead of drawing T-accounts
              with spreadsheets or whiteboard.
            </Typography>

            <Paper
              elevation={0}
              sx={{
                p: { xs: 2, md: 3 },
                borderRadius: 3,
                border: "1px solid #d9dee3",
                backgroundColor: "#fff",
                mb: 4,
              }}
            >
              <Box
                component="img"
                src="/t-accounts-app-screenshot.png"
                alt="T-Accounts application"
                sx={{
                  width: "100%",
                  height: "auto",
                  borderRadius: 2,
                  display: "block",
                }}
              />
            </Paper>

            {shouldShowTryForFree && (
              <Button
                variant="contained"
                onClick={handleTryForFree}
                disabled={loading}
                sx={{
                  px: 3,
                  py: 1,
                  borderRadius: 2,
                  backgroundColor: "#4b7bec",
                  color: "#fff",
                  fontWeight: 600,
                  fontSize: "0.9rem",
                  textTransform: "none",
                  boxShadow: "0 2px 8px 0 rgba(75, 123, 236, 0.18)",
                  '&:hover, &:focus': {
                    backgroundColor: "#3d68cf",
                    boxShadow: "0 4px 16px 0 rgba(75, 123, 236, 0.24)",
                  },
                }}
              >
                {loading ? <CircularProgress size={24} color="inherit" /> : "Try It Free"}
              </Button>
            )}
          </Container>
        </Box>

        <Box component="section" sx={{ py: { xs: 8, md: 10 }, backgroundColor: "#fff", color: "#111827" }}>
          <Container maxWidth="md" sx={{ textAlign: "center" }}>
            <Typography variant="h3" sx={{ fontWeight: 700, mb: 3, fontSize: { xs: "1.9rem", md: "2.4rem" } }}>
              The Problem
            </Typography>
            <Typography sx={{ mb: 2, fontSize: { xs: "0.95rem", md: "1rem" } }}>
              Many accountants still analyze entries using Excel spreadsheets, whiteboards, or paper notes.
            </Typography>
            <Typography sx={{ color: "#5b6470", fontSize: { xs: "0.95rem", md: "1rem" } }}>
              T-Accounts modernizes this process by letting you type commands and instantly visualize debits and
              credits.
            </Typography>

            <Paper
              elevation={0}
              sx={{
                p: { xs: 2, md: 3 },
                borderRadius: 3,
                border: "1px solid #d9dee3",
                backgroundColor: "#fff",
                mt: 4,
              }}
            >
              {showVideoFallback ? (
                <Box
                  sx={{
                    width: "100%",
                    minHeight: { xs: 220, md: 360 },
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderRadius: 2,
                    border: "1px dashed #b7c0c8",
                    backgroundColor: "#fafbfc",
                  }}
                >
                  <Typography sx={{ color: "#5b6470" }}>T-Accounts application demo</Typography>
                </Box>
              ) : (
                <Box
                  component="video"
                  src="/T-AccountsHomePageVideo.mp4"
                  aria-label="T-Accounts application demo"
                  autoPlay
                  muted
                  loop
                  playsInline
                  onError={() => setShowVideoFallback(true)}
                  sx={{
                    width: "100%",
                    height: "auto",
                    borderRadius: 2,
                    display: "block",
                  }}
                />
              )}
            </Paper>
          </Container>
        </Box>

        <Box component="section" sx={{ py: { xs: 7, md: 8 }, backgroundColor: "#f4f6f7", color: "#111827" }}>
          <Container maxWidth="md" sx={{ textAlign: "center" }}>
            <Typography variant="h3" sx={{ fontWeight: 700, mb: 4, fontSize: { xs: "1.8rem", md: "2.15rem" } }}>
              Who Uses T-Accounts
            </Typography>

            {audiences.map((audience) => (
              <Box key={audience.title} sx={{ mb: 2.8 }}>
                <Typography sx={{ fontWeight: 700, fontSize: { xs: "1.05rem", md: "1.2rem" }, mb: 0.7 }}>
                  {audience.title}
                </Typography>
                <Typography sx={{ color: "#5b6470", fontSize: { xs: "0.92rem", md: "0.98rem" } }}>
                  {audience.description}
                </Typography>
              </Box>
            ))}
          </Container>
        </Box>

        <Box component="section" sx={{ py: { xs: 8, md: 10 }, color: "#111827" }}>
          <Container maxWidth="lg" sx={{ textAlign: "center" }}>
            <Typography variant="h3" sx={{ fontWeight: 700, mb: 5, fontSize: { xs: "1.8rem", md: "2.15rem" } }}>
              Features
            </Typography>

            <Grid container spacing={2.5} justifyContent="center">
              {features.map((feature) => (
                <Grid
                  key={feature.title}
                  item
                  xs={12}
                  sm={6}
                  md={4}
                  sx={{
                    flexBasis: { lg: "20%" },
                    maxWidth: { lg: "20%" },
                  }}
                >
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2.4,
                      height: "100%",
                      backgroundColor: "#fff",
                      border: "1px solid #e2e7eb",
                      borderRadius: 2,
                    }}
                  >
                    <Typography sx={{ fontWeight: 700, mb: 1, fontSize: { xs: "1rem", md: "1.05rem" } }}>
                      {feature.title}
                    </Typography>
                    <Typography sx={{ color: "#5b6470", fontSize: { xs: "0.9rem", md: "0.94rem" } }}>
                      {feature.description}
                    </Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>

        <Box component="footer" sx={{ py: 5, backgroundColor: "#111418" }}>
          <Container maxWidth="md" sx={{ textAlign: "center" }}>
            <Typography sx={{ color: "#fff", fontSize: "1.1rem" }}>
              T-Accounts — Analyze journal entries the modern way.
            </Typography>
            <Box sx={{ mt: 1.1, color: "rgba(255, 255, 255, 0.86)" }}>
              <LegalLinks centered />
            </Box>
          </Container>
        </Box>
      </Box>
    </Box>
  );
}
